import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';
import type { SessionRecordingSummary, VideoFile } from '@/types';

const RECORDING_STATUS_POLL_INTERVAL_MS = 15_000;

/**
 * Metadata-only recordings poll for the session detail page (issue #1000):
 * the page no longer plays anything, so it never requests playback URLs —
 * playback happens in /watch and the analytics tool.
 *
 * `availableVideos`/`availableVideoIds` come from the parent's
 * VideoFilesList-driven upload state — passed in rather than owned here so
 * the parent doesn't need two sources of truth for "which videos exist."
 */
export function useSessionRecordings(
  sessionId: string,
  availableVideos: VideoFile[],
  availableVideoIds: string,
) {
  const [recordings, setRecordings] = useState<SessionRecordingSummary[]>([]);
  const [areRecordingsLoading, setAreRecordingsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let pollTimeout: ReturnType<typeof setTimeout> | undefined;

    const fetchRecordings = async () => {
      try {
        const data = await apiClient.getSessionRecordings(sessionId, {
          includePlayback: false,
        });
        if (cancelled) {
          return;
        }

        const nextRecordings = data.recordings || [];
        setRecordings(nextRecordings);
        setAreRecordingsLoading(false);

        const recordedVideoIds = new Set(
          nextRecordings.flatMap((recording) =>
            recording.chapters.map((chapter) => chapter.id),
          ),
        );
        const hasUnassembledVideo = availableVideos.some(
          (video) => !recordedVideoIds.has(video.id),
        );
        const hasUnresolvedRecording = nextRecordings.some(
          (recording) =>
            recording.playbackStatus === 'pending' ||
            recording.playbackStatus === 'partial',
        );

        if (hasUnassembledVideo || hasUnresolvedRecording) {
          pollTimeout = setTimeout(
            fetchRecordings,
            RECORDING_STATUS_POLL_INTERVAL_MS,
          );
        }
      } catch (error) {
        console.error('Error fetching session recordings:', error);
        if (!cancelled) {
          setAreRecordingsLoading(false);
        }
        if (!cancelled && availableVideos.length > 0) {
          pollTimeout = setTimeout(
            fetchRecordings,
            RECORDING_STATUS_POLL_INTERVAL_MS,
          );
        }
      }
    };

    void fetchRecordings();
    return () => {
      cancelled = true;
      if (pollTimeout) {
        clearTimeout(pollTimeout);
      }
    };
    // availableVideoIds is a stable, sorted digest of availableVideos — it
    // (not the array reference) is what should re-trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableVideoIds, sessionId]);

  return { recordings, areRecordingsLoading };
}
